import { TableResponse } from 'components/Table/types'
import { Env } from 'config/Env'
import { Envelope } from 'utils/types/Envelope'

import { HttpClient, ApiRequestTypes } from '../../../utils/apiService/httpService'
import { NotificationsParam, UpdateNotificationParam } from '../types'
import {
  GroupedNotificationsRequest,
  GroupedNotificationsResponse,
  MarkAllReadResponse,
  MarkReferralReadRequest,
  MarkReferralReadResponse,
  NotificationScope,
  UnreadCountResponse,
} from '../types/grouped'

import {
  getGroupedNotificationsFixture,
  getGroupedUnreadCountFixture,
  markAllReadFixture,
  markReferralReadFixture,
  USE_GROUPED_NOTIFICATIONS_FIXTURE,
} from './groupedNotifications.fixture'

const GET_NOTIFICATIONSLIST = `${Env.REACT_APP_API_URL}Referral/api/Notification/get`
const SET_NOTIFICATION_READ_STATUS = `${Env.REACT_APP_API_URL}Referral/api/Notification/UpdateNotifications`

export const getNotificationsList = (body: NotificationsParam): Promise<TableResponse> =>
  HttpClient[ApiRequestTypes.post](GET_NOTIFICATIONSLIST, body)
export const setNotificationsReadStatus = (body: UpdateNotificationParam): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](SET_NOTIFICATION_READ_STATUS, body)

// Grouped notification bell and route — Notification Rework (US 18303).
const GET_GROUPED_NOTIFICATIONS = `${Env.REACT_APP_API_URL}Referral/api/Notification/GetGrouped`
const GET_GROUPED_UNREAD_COUNT = `${Env.REACT_APP_API_URL}Referral/api/Notification/UnreadCount`
const MARK_REFERRAL_READ = `${Env.REACT_APP_API_URL}Referral/api/Notification/MarkReferralRead`
const MARK_ALL_READ = `${Env.REACT_APP_API_URL}Referral/api/Notification/MarkAllRead`

/**
 * One page of grouped notifications — one entry per referral.
 *
 * `unreadOnly` picks the surface: the bell asks for outstanding referrals, the route for
 * read and unread together. Offset-paged with a real `totalCount` because the screen shows
 * "Showing 13 of 32". No user id is sent; the backend derives it from the token.
 * @param data 1-based page number, an explicit page size and the unread-only flag.
 */
export const getGroupedNotifications = (
  data: GroupedNotificationsRequest,
): Promise<GroupedNotificationsResponse> =>
  USE_GROUPED_NOTIFICATIONS_FIXTURE
    ? getGroupedNotificationsFixture(data)
    : HttpClient[ApiRequestTypes.post](GET_GROUPED_NOTIFICATIONS, data)

/**
 * Query string for the scope on a GET. ASP.NET binds a list from repeated keys, which axios
 * would instead send as `applicableCommunityList[]`.
 */
const toScopeQuery = (scope: NotificationScope): string => {
  const query = new URLSearchParams({ userLevel: String(scope.userLevel) })
  scope.applicableCommunityList.forEach(id => query.append('applicableCommunityList', String(id)))
  return query.toString()
}

/** Bell badge count — referrals with unread activity, not individual events. */
export const getGroupedUnreadCount = (scope: NotificationScope): Promise<UnreadCountResponse> =>
  USE_GROUPED_NOTIFICATIONS_FIXTURE
    ? getGroupedUnreadCountFixture()
    : HttpClient[ApiRequestTypes.get](`${GET_GROUPED_UNREAD_COUNT}?${toScopeQuery(scope)}`)

/** Clears every unread notification the caller holds for one referral. Idempotent. */
export const markReferralNotificationsRead = (
  data: MarkReferralReadRequest,
): Promise<MarkReferralReadResponse> =>
  USE_GROUPED_NOTIFICATIONS_FIXTURE
    ? markReferralReadFixture(data.patientId)
    : HttpClient[ApiRequestTypes.post](MARK_REFERRAL_READ, data)

/**
 * Clears every unread notification the caller holds in the scope they are viewing,
 * referral-bound or not, in one call. Rows stay in the route's listing, read; nothing is deleted.
 */
export const markAllNotificationsRead = (scope: NotificationScope): Promise<MarkAllReadResponse> =>
  USE_GROUPED_NOTIFICATIONS_FIXTURE
    ? markAllReadFixture()
    : HttpClient[ApiRequestTypes.post](MARK_ALL_READ, scope)
